import marketdata from "../../data/Services/marketdata";

// ============================================================================
// Viewport re-anchor on data replacement
// ============================================================================
//
// `xExtents` is an ABSOLUTE index domain ([startIndex, endIndex]) — it indexes
// array POSITION, not time. That position is not stable when the data array is
// replaced: real-time appends grow the in-memory array without bound, but every
// (re)load refetches a fixed window (getBars caps at 2000 bars). So the same
// "last bar" comes back at a SMALLER index after a reload, and a saved/live
// right edge ends up N indices past the real last bar — the chart renders as if
// panned left by N (N = bars appended since the array was last fetched at the
// reload size). This is the "chart drifts left after refresh" bug.
//
// Fix: whenever the array is replaced (boot restore, gap auto-reload, reconnect),
// shift the domain by delta = newNewest - oldNewest so the viewport keeps its
// offset from the last bar. Flush-right (offset 0) stays flush; a deliberate
// parked-left/zoom keeps its exact gap. `oldNewest` comes from the live array
// when present (in-session refetch) or from the persisted xExtentsLastIndex
// anchor (boot, when the browser refresh wiped the in-memory array).
//
// @param {Array|null} storedXExtents  current absolute [start, end] or null
// @param {number|null} oldNewest  last index the stored domain is valid for
// @param {number} newLength  length of the replacement array
// @returns {{xExtents:number[], xExtentsLastIndex:number}|null} null = no change
export function rebaseXExtentsForReplace(storedXExtents, oldNewest, newLength) {
  if (!Array.isArray(storedXExtents) || storedXExtents.length !== 2)
    return null;
  if (oldNewest === null || oldNewest === undefined) return null;
  const newNewest = newLength - 1;
  if (newNewest < 0) return null;
  const delta = newNewest - oldNewest;
  if (delta === 0) return null;
  return {
    xExtents: [storedXExtents[0] + delta, storedXExtents[1] + delta],
    xExtentsLastIndex: newNewest,
  };
}

// Resolve the "old newest index" that a viewport's absolute xExtents is valid
// for, at the moment its data array is replaced. Priority:
//   1. live array present → its last index (in-session refetch: gap/reconnect).
//   2. persisted anchor   → xExtentsLastIndex (desktop saved by current code).
//   3. legacy (no anchor) → the saved RIGHT EDGE, i.e. assume the chart was
//      following the last bar so it snaps flush-right on load (width preserved).
//      Legacy absolute domains can't be re-based exactly — the save-time last
//      index is lost — so flush is the pragmatic default; re-saving then captures
//      a real anchor and preserves deliberate pan/zoom thereafter. This is what
//      materializes the anchor for old desktops (local/remote/autosave) on first
//      load instead of letting them drift once.
// Returns null when there is nothing to anchor (no usable xExtents).
//
// @param {Array|undefined} oldData  the array being replaced (live data, if any)
// @param {Array|null} storedXExtents  current absolute [start, end] or null
// @param {number|null|undefined} persistedLastIndex  saved xExtentsLastIndex
// @returns {number|null}
export function resolveReplaceAnchor(
  oldData,
  storedXExtents,
  persistedLastIndex,
) {
  if (oldData && oldData.length > 0) return oldData.length - 1;
  if (typeof persistedLastIndex === "number") return persistedLastIndex;
  if (Array.isArray(storedXExtents) && storedXExtents.length === 2)
    return storedXExtents[1];
  return null;
}

// ============================================================================
// History↔RT seam reconcile
// ============================================================================
//
// When a chart loads, getBars returns a snapshot whose in-progress (seam) bar
// already counts every trade up to order number N (= the bar's cumulative NEG,
// exposed as `N`). Real-time quotes that arrived during the fetch used to be
// dropped (handleQuoteData skips `isLoading` charts), and afterwards each
// coalesced quote just `+= Q` onto the server's partial bar — with no dedup by
// ON between the snapshot's last trade and the first RT trade. That left the
// seam bar's volume off by the trades that fell in the fetch window.
//
// Fix: while the chart loads, tap the RAW (pre-coalescing) per-trade feed into a
// per-chart bag. Coalesced quotes sum volume and can't be split at the snapshot
// boundary, so raw per-trade ON is required for an exact cut. Once history
// resolves we know X = seam bar's ON; we then hand the chart back to the shared
// coalesced stream aligned to a coalesced-window boundary (consecutive worker
// windows are ON-contiguous, and a coalesced quote's `ON` is its window's FIRST
// raw ON): on the first coalesced quote whose firstON > X we apply the bagged
// raw trades in (X, firstON) and that quote, then release. No gap, no overlap.
//
// The bag/gate is strictly per-chart (keyed by chart.id); other charts on the
// same symbol keep quoting live. The worker's per-symbol coalescing is untouched.

const seamReconcilers = new Map(); // chart.id → reconciler
const SEAM_BAG_MAX = 20000; // hard cap on buffered raw trades (overflow → fall back)
const SEAM_TIMEOUT_MS = 15000; // give up waiting for the coalesced boundary

function round2(n) {
  return Number.isFinite(n) ? Math.round((n + Number.EPSILON) * 100) / 100 : n;
}

/**
 * Begin buffering raw trades for a chart that is about to load history.
 * Clears any prior reconciler for the same chart (reload). Returns the
 * reconciler, or null when raw taps are unavailable.
 */
function startSeamReconcile(chartId, symbol) {
  cancelSeamReconcile(chartId);
  if (!marketdata || typeof marketdata.registerRawTap !== "function")
    return null;
  const rec = {
    chartId,
    symbol,
    X: null, // snapshot seam-bar ON; set once history is applied
    armed: false, // true once history applied and we await the coalesced boundary
    bag: [], // raw trades {ON,O,H,L,C,Q,F,TC,DT} in arrival order
    overflow: false,
    startedAt: Date.now(),
    untap: null,
  };
  rec.untap = marketdata.registerRawTap(symbol, (q) => {
    if (q.ON == null) return; // need ON to dedup
    if (rec.bag.length >= SEAM_BAG_MAX) {
      rec.overflow = true;
      return;
    }
    rec.bag.push({
      ON: q.ON,
      O: round2(q.O),
      H: round2(q.H),
      L: round2(q.L),
      C: round2(q.C),
      Q: q.Q,
      F: q.F,
      TC: q.TC,
      DT: q.DT,
    });
  });
  seamReconcilers.set(chartId, rec);
  return rec;
}

/**
 * Tear down a chart's reconciler: stop the raw tap and forget it. Idempotent.
 */
export function cancelSeamReconcile(chartId) {
  const rec = seamReconcilers.get(chartId);
  if (!rec) return;
  if (typeof rec.untap === "function") {
    try {
      rec.untap();
    } catch {
      /* tap already gone */
    }
  }
  seamReconcilers.delete(chartId);
}

/**
 * Apply bagged raw trades with loON < ON < hiON to `data`, in ON order.
 * Returns true if any trade changed the data.
 */
function applyBaggedTrades(rec, data, periodicity, symbol, loON, hiON) {
  const trades = rec.bag
    .filter((t) => t.ON > loON && t.ON < hiON)
    .sort((a, b) => a.ON - b.ON);
  let changed = false;
  for (let i = 0; i < trades.length; i++) {
    if (mergeQuotesInPlace(data, trades[i], periodicity, symbol))
      changed = true;
  }
  return changed;
}

/**
 * Route a coalesced quote for a reconciling chart. Returns the mergeQuotesInPlace
 * change type ('updated' | 'added' | null) so the caller bumps dataVersion, and
 * finalizes the reconcile once the chart is handed back to the live stream.
 */
function reconcileSeamCoalesced(rec, data, quote, chart) {
  const periodicity = chart.periodicity;
  const firstON = quote.ON;

  // Timeout safety: never hold the gate open indefinitely. Drain every bagged
  // trade past the snapshot and release; drop the triggering coalesced quote
  // (the raw bag, which runs ahead of the worker, already covers its trades).
  if (Date.now() - rec.startedAt > SEAM_TIMEOUT_MS || firstON == null) {
    const changed = applyBaggedTrades(
      rec,
      data,
      periodicity,
      chart.symbol,
      rec.X,
      Infinity,
    );
    cancelSeamReconcile(rec.chartId);
    return changed ? "updated" : null;
  }

  // The coalesced window starts within the already-counted region. Its trades
  // past X are also in the raw bag and get applied at release — drop for now.
  if (firstON <= rec.X) return null;

  // First coalesced window fully after the snapshot: apply the bagged raw trades
  // in (X, firstON) — the seam catch-up — then this quote, then release.
  let changed = applyBaggedTrades(
    rec,
    data,
    periodicity,
    chart.symbol,
    rec.X,
    firstON,
  );
  if (mergeQuotesInPlace(data, quote, periodicity, chart.symbol))
    changed = true;
  cancelSeamReconcile(rec.chartId);
  return changed ? "updated" : null;
}

// Time interval parsing functions
export function parseInterval(interval) {
  if (interval === "D") return "day";
  if (interval === "W") return "week";
  if (interval === "M") return "month";
  if (interval === "min") return "minute";
  if (interval === "h") return "hour";
  return "";
}

export function getBarTime(quote, periodicity, symbol = null) {
  const quoteTime = new Date(quote.DT);
  switch (periodicity) {
    case "1D":
      // Use setUTCHours so the bar key is always 21:50 UTC (= 18:50 BRT) regardless
      // of the user's local timezone. setHours() uses local time, producing a different
      // UTC timestamp for non-Brazilian users and causing a duplicate bar (#184).
      // Brazil stopped observing DST in 2019, so BRT is always UTC-3 = 21:50 UTC.
      quoteTime.setUTCHours(21, 50, 0, 0);
      break;
    case "1W": {
      const day = quoteTime.getDay();
      const diff = quoteTime.getDate() - day + (day === 0 ? -6 : 1);
      quoteTime.setDate(diff);
      quoteTime.setHours(0, 0, 0, 0);
      break;
    }
    case "1M":
      quoteTime.setDate(1);
      quoteTime.setHours(0, 0, 0, 0);
      break;
    case "1min":
      quoteTime.setSeconds(0, 0);
      break;
    case "2min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 2) * 2, 0, 0);
      break;
    case "3min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 3) * 3, 0, 0);
      break;
    case "4min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 4) * 4, 0, 0);
      break;
    case "5min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 5) * 5, 0, 0);
      break;
    case "10min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 10) * 10, 0, 0);
      break;
    case "15min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 15) * 15, 0, 0);
      break;
    case "20min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 20) * 20, 0, 0);
      break;
    case "30min":
      quoteTime.setMinutes(Math.floor(quoteTime.getMinutes() / 30) * 30, 0, 0);
      break;
    case "60min":
      quoteTime.setMinutes(0, 0, 0);
      break;
    case "120min": {
      // 2-hour bars: align to market open hour (9h for BMF, 10h for BOVESPA)
      const marketOpenHour = symbol ? marketdata.getMarketOpenHour(symbol) : 10;
      const currentHour = quoteTime.getHours();
      const hoursSinceOpen = currentHour - marketOpenHour;
      const barIndex = Math.floor(hoursSinceOpen / 2);
      const alignedHour = marketOpenHour + barIndex * 2;
      quoteTime.setHours(alignedHour, 0, 0, 0);
      break;
    }
    case "240min": {
      // 4-hour bars: align to market open hour (9h for BMF, 10h for BOVESPA)
      const marketOpenHour = symbol ? marketdata.getMarketOpenHour(symbol) : 10;
      const currentHour = quoteTime.getHours();
      const hoursSinceOpen = currentHour - marketOpenHour;
      const barIndex = Math.floor(hoursSinceOpen / 4);
      const alignedHour = marketOpenHour + barIndex * 4;
      quoteTime.setHours(alignedHour, 0, 0, 0);
      break;
    }
    default:
      throw new Error(`Unsupported periodicity: ${periodicity}`);
  }
  return quoteTime;
}

// Helper to extract numeric volume
function getNumericVolume(vol) {
  if (typeof vol === "number") return vol;
  if (vol && typeof vol.volume === "number") return vol.volume;
  return 0;
}

/**
 * mergeQuotesInPlace - PERFORMANCE OPTIMIZED VERSION
 *
 * Mutates the data array in-place instead of creating a new array on every quote.
 * This eliminates 48MB/sec of allocations during 60 quotes/sec replay mode.
 *
 * Returns: 'updated' | 'added' | null
 * - 'updated': Existing bar was updated
 * - 'added': New bar was added
 * - null: No changes made
 *
 * IMPORTANT: This function intentionally mutates the input array for performance.
 * React re-renders are triggered by incrementing the chart's dataVersion counter.
 */
export function mergeQuotesInPlace(data, quote, periodicity, symbol = null) {
  if (!data || data.length === 0) return null;

  const barTime = getBarTime(quote, periodicity, symbol);

  // Get FOQ multiplier for derivatives (stocks have FOQ=1 or undefined)
  // Backend sends historical F already adjusted by FOQ, but real-time quote.F is raw
  const symbolData = symbol ? marketdata.getSymbol(symbol) : null;
  const foq =
    symbolData && typeof symbolData.FOQ === "number" ? symbolData.FOQ : 1;
  const adjustedF = (quote.F || 0) * foq;
  const lastBar = data[data.length - 1];

  if (lastBar.date.getTime() === barTime.getTime()) {
    // Update existing bar in-place (no new object allocation).
    // Guard with Number.isFinite -- `!== null` accepts undefined/NaN/+-Infinity,
    // and a non-finite O/H/L/C poisons the price scale and the Renko auto
    // brick size (issue #323).
    // A traded price is itself a high/low candidate. The server omits H/L on
    // flat single-price ticks (TickHigh==TickLow==Last), so when the feed runs
    // uncoalesced (raw FT= ticks) a bar built only from those would never grow a
    // wick. Fold C into high/low so the wick forms from trade prices regardless
    // of whether H/L are present (marketdata#17).
    if (Number.isFinite(quote.C)) {
      lastBar.close = quote.C;
      lastBar.high = Math.max(lastBar.high, quote.C);
      lastBar.low = Math.min(lastBar.low, quote.C);
    }
    if (Number.isFinite(quote.H))
      lastBar.high = Math.max(lastBar.high, quote.H);
    if (Number.isFinite(quote.L)) lastBar.low = Math.min(lastBar.low, quote.L);

    // IMPORTANT: SignalR sends INCREMENTAL volume data (per-trade deltas, NOT cumulative)
    // According to MarketData source (ConnectionsManager.cs):
    //   - Q = TradeVolume (volume of THIS TRADE ONLY)
    //   - F = TradeFinancialVolume (financial volume of THIS TRADE ONLY)
    //   - TC = OrderNumber delta (tick count of THIS TRADE ONLY)
    // Therefore: ALWAYS ACCUMULATE these values to the bar (no delta calculation needed)

    // IMPORTANT: The volume indicator transforms volume to {volume: number}
    // We must preserve this structure to avoid bouncing between number and object
    const newVolume = getNumericVolume(lastBar.volume) + (quote.Q || 0);
    if (
      lastBar.volume &&
      typeof lastBar.volume === "object" &&
      "volume" in lastBar.volume
    ) {
      lastBar.volume.volume = newVolume;
    } else {
      lastBar.volume = newVolume;
    }

    const newFinancialVolume =
      getNumericVolume(lastBar.financialVolume) + adjustedF;
    if (
      lastBar.financialVolume &&
      typeof lastBar.financialVolume === "object" &&
      "volume" in lastBar.financialVolume
    ) {
      lastBar.financialVolume.volume = newFinancialVolume;
    } else {
      lastBar.financialVolume = newFinancialVolume;
    }

    const newTickCount = getNumericVolume(lastBar.tickCount) + (quote.TC || 0);
    if (
      lastBar.tickCount &&
      typeof lastBar.tickCount === "object" &&
      "volume" in lastBar.tickCount
    ) {
      lastBar.tickCount.volume = newTickCount;
    } else {
      lastBar.tickCount = newTickCount;
    }

    return "updated";
  } else {
    // New bar needed - only allocate when necessary.
    // Fall back to a finite close for any non-finite O/H/L/C -- never seed
    // a bar with undefined/NaN/+-Infinity (issue #323).
    const c = Number.isFinite(quote.C) ? quote.C : lastBar.close;
    data.push({
      date: barTime,
      open: Number.isFinite(quote.O) ? quote.O : c,
      high: Number.isFinite(quote.H) ? quote.H : c,
      low: Number.isFinite(quote.L) ? quote.L : c,
      close: c,
      volume: quote.Q || 0,
      financialVolume: adjustedF,
      tickCount: quote.TC || 0,
    });
    return "added";
  }
}

/**
 * mergeQuotes - LEGACY VERSION (kept for compatibility)
 *
 * This creates a new array on every quote, causing 48MB/sec allocations.
 * Use mergeQuotesInPlace() instead for better performance.
 *
 * @deprecated Use mergeQuotesInPlace() for better performance
 */
export function mergeQuotes(data, quote, periodicity, symbol = null) {
  if (!data || data.length === 0) return undefined;
  const barTime = getBarTime(quote, periodicity, symbol);
  const newData = [...data];
  const lastBar = newData[newData.length - 1];

  // Get FOQ multiplier for derivatives (same as mergeQuotesInPlace)
  const symbolData = symbol ? marketdata.getSymbol(symbol) : null;
  const foq =
    symbolData && typeof symbolData.FOQ === "number" ? symbolData.FOQ : 1;
  const adjustedF = (quote.F || 0) * foq;

  if (lastBar.date.getTime() === barTime.getTime()) {
    newData[newData.length - 1] = {
      ...lastBar,
      // Guard with Number.isFinite -- non-finite O/H/L/C must never reach a bar (#323).
      close: Number.isFinite(quote.C) ? quote.C : lastBar.close,
      high: Number.isFinite(quote.H)
        ? Math.max(lastBar.high, quote.H)
        : lastBar.high,
      low: Number.isFinite(quote.L)
        ? Math.min(lastBar.low, quote.L)
        : lastBar.low,
      // SignalR sends INCREMENTAL volume data (per-trade deltas), so ACCUMULATE all fields
      volume: getNumericVolume(lastBar.volume) + (quote.Q || 0),
      financialVolume: getNumericVolume(lastBar.financialVolume) + adjustedF,
      tickCount: getNumericVolume(lastBar.tickCount) + (quote.TC || 0),
    };
  } else {
    // Fall back to a finite close for any non-finite O/H/L/C (issue #323).
    const c = Number.isFinite(quote.C) ? quote.C : lastBar.close;
    newData.push({
      date: barTime,
      open: Number.isFinite(quote.O) ? quote.O : c,
      high: Number.isFinite(quote.H) ? quote.H : c,
      low: Number.isFinite(quote.L) ? quote.L : c,
      close: c,
      volume: quote.Q || 0,
      financialVolume: adjustedF,
      tickCount: quote.TC || 0,
    });
  }
  return newData;
}

// Data fetching functions
export async function fetchChartData(chart, updateChartConfig, setData) {
  updateChartConfig(chart.id, { isLoading: true, error: null });

  // Declared out here so the catch can identity-guard it (see below).
  let seamRec = null;

  try {
    let periodicity = chart.periodicity;
    let period = periodicity.match(/\d+/g)?.[0];
    let interval = periodicity.match(/[a-zA-Z]+/g)?.[0];
    let parsedInterval = parseInterval(interval);

    marketdata.registerQuote(chart.symbol, `chart-${chart.id}`);

    // Start buffering raw trades for the history↔RT seam reconcile (intraday
    // only — daily/week/month bars carry no per-bar cumulative ON and have no
    // sub-minute seam). The bag fills while getBars is in flight.
    const isIntraday = parsedInterval === "minute" || parsedInterval === "hour";
    seamRec = isIntraday ? startSeamReconcile(chart.id, chart.symbol) : null;

    let endDate = new Date();
    endDate.setDate(endDate.getDate() + 1);

    let startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 10);

    // Use debug test bars count if set, otherwise default to 2000 for incremental loading
    const ticksCount = import.meta.env.VITE_DEBUG_TEST_BARS
      ? parseInt(import.meta.env.VITE_DEBUG_TEST_BARS, 10)
      : 2000;

    const response = await marketdata.getBars(
      chart.symbol,
      startDate,
      endDate,
      {
        chartId: chart.id, // Used to abort previous in-flight request for same chart
        periodicity: parsedInterval, // OdinMD: MINUTE, DAY, WEEK, MONTH, YEAR (uppercase)
        interval: period, // OdinMD: Multiplier for MINUTE periodicity
        barscount: ticksCount, // OdinMD: Number of bars to fetch (1-16000)
        aftermarket: false, // OdinMD: Include extended hours
        adjusted: true, // OdinMD: Apply corporate actions adjustments
      },
    );

    // Request was aborted by a newer request — bail out. Only tear down the
    // reconciler if it is still OURS: a newer load (e.g. periodicity change)
    // has already replaced it via startSeamReconcile, and cancelling by chartId
    // here would destroy the newer load's reconciler + bag (the seam −N race).
    if (response.aborted) {
      if (seamRec && seamReconcilers.get(chart.id) === seamRec)
        cancelSeamReconcile(chart.id);
      return;
    }

    const data = response.bars.map((d) => {
      let date = new Date(d.T * 1000);

      // Issue #102: Normalize weekly/monthly bar dates to match getBarTime() format
      // Issue #184: Normalize daily bar dates to 21:50 UTC (= 18:50 BRT) — same as getBarTime("1D")
      // This ensures real-time quotes merge correctly with historical bars for all timezones
      if (parsedInterval === "day") {
        date.setUTCHours(21, 50, 0, 0);
      } else if (parsedInterval === "week") {
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        date.setDate(diff);
        date.setHours(0, 0, 0, 0);
      } else if (parsedInterval === "month") {
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
      }

      return {
        date,
        open: +d.O,
        high: +d.H,
        low: +d.L,
        close: +d.C,
        volume: +d.V,
        // Enhanced fields from OdinMD V3 API
        financialVolume: +(d.F || 0), // Financial volume (monetary value)
        tickCount: +(d.TC || 0), // Number of trades
      };
    });

    setData(chart.id, data);

    // Arm the seam reconcile: X = the in-progress (last) bar's cumulative ON
    // (field `N`). Once armed, handleQuoteData hands the chart's coalesced
    // stream to reconcileSeamCoalesced (isLoading flips false in `finally`).
    // No N (backend not yet exposing it), no trades buffered, or overflow →
    // drop the reconcile and fall back to plain merge.
    // Identity guard: only arm if this load's reconciler is still the current
    // one — a newer load may have superseded it while history was in flight.
    if (seamRec && seamReconcilers.get(chart.id) === seamRec) {
      const lastRaw = response.bars[response.bars.length - 1];
      const seamON = lastRaw && Number.isFinite(+lastRaw.N) ? +lastRaw.N : 0;
      if (seamON > 0 && !seamRec.overflow) {
        seamRec.X = seamON;
        seamRec.armed = true;
      } else {
        cancelSeamReconcile(chart.id);
      }
    }

    // Reset hasMoreData flag to allow incremental loading
    updateChartConfig(chart.id, { hasMoreData: true });
  } catch (error) {
    if (seamRec && seamReconcilers.get(chart.id) === seamRec)
      cancelSeamReconcile(chart.id);
    updateChartConfig(chart.id, { error: error.message });
  } finally {
    updateChartConfig(chart.id, { isLoading: false });
  }
}

/**
 * loadMoreBars - Load older historical bars incrementally
 * @param {Object} chart - Chart configuration
 * @param {Array} chartData - Current chart data array (from chartData map)
 * @param {number} barsToLoad - Number of bars to load (default: 2000)
 * @returns {Promise<Array>} - Array of older bars
 */
export async function loadMoreBars(chart, chartData, barsToLoad = 2000) {
  if (!chartData || chartData.length === 0) {
    console.warn("[loadMoreBars] No data to load more from");
    return [];
  }

  const oldestBar = chartData[0];
  const oldestDate = new Date(oldestBar.date);

  console.log(
    `[loadMoreBars] Loading ${barsToLoad} bars before ${oldestDate.toISOString()}`,
  );

  try {
    let periodicity = chart.periodicity;
    let period = periodicity.match(/\d+/g)?.[0];
    let interval = periodicity.match(/[a-zA-Z]+/g)?.[0];
    let parsedInterval = parseInterval(interval);

    let startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 10);

    const response = await marketdata.getBars(
      chart.symbol,
      startDate,
      oldestDate, // Fetch bars BEFORE this date
      {
        periodicity: parsedInterval,
        interval: period,
        barscount: barsToLoad,
        aftermarket: false,
        adjusted: true,
      },
    );

    const newBars = response.bars
      .map((d) => {
        let date = new Date(d.T * 1000);

        // Issue #102: Normalize weekly/monthly bar dates to match getBarTime() format
        // Issue #184: Normalize daily bar dates to 21:50 UTC (= 18:50 BRT)
        if (parsedInterval === "day") {
          date.setUTCHours(21, 50, 0, 0);
        } else if (parsedInterval === "week") {
          const day = date.getDay();
          const diff = date.getDate() - day + (day === 0 ? -6 : 1);
          date.setDate(diff);
          date.setHours(0, 0, 0, 0);
        } else if (parsedInterval === "month") {
          date.setDate(1);
          date.setHours(0, 0, 0, 0);
        }

        return {
          date,
          open: +d.O,
          high: +d.H,
          low: +d.L,
          close: +d.C,
          volume: +d.V,
          financialVolume: +(d.F || 0),
          tickCount: +(d.TC || 0),
        };
      })
      .filter((bar) => bar.date < oldestDate); // Remove duplicates

    console.log(
      `[loadMoreBars] Loaded ${newBars.length} new bars (${response.bars.length - newBars.length} duplicates filtered)`,
    );

    return newBars;
  } catch (error) {
    console.error("[loadMoreBars] Error loading more bars:", error);
    return [];
  }
}

/**
 * handleQuoteData - PERFORMANCE OPTIMIZED VERSION (Data Separation)
 *
 * Updates chartData map instead of charts array to prevent Workspace re-renders.
 * Uses mergeQuotesInPlace() to mutate data array - same as original handleQuote.
 * Increments dataVersion to trigger React re-renders.
 *
 * Performance impact:
 * - Before: Workspace re-renders on every quote (60+ times/sec)
 * - After: Only ChartWrapper re-renders, Workspace stays stable
 *
 * @param {Object} quote - The incoming quote data
 * @param {Array} charts - Charts array (for symbol matching and loading state)
 * @param {Object} chartData - Current chartData map { [chartId]: { data: [], dataVersion: 0 } }
 * @param {string} periodicity - Optional periodicity override
 * @returns {Object} - { chartData, charts } — same references when nothing changed.
 *   `charts` only gets a new reference when a parked-left viewport has to follow
 *   a new bar (issue #163); otherwise it stays === input so the Workspace does
 *   not re-render on routine quotes.
 */
export function handleQuoteData(quote, charts, chartData, periodicity) {
  // PERFORMANCE: Use for loop and only clone map if changes detected
  // This prevents Zustand from triggering subscribers when quote doesn't affect any chart
  let newChartData = null;
  // Only cloned when a new bar forces a parked-left viewport to follow (#163).
  let newCharts = null;

  for (let i = 0; i < charts.length; i++) {
    const chart = charts[i];
    // Family-aware matching: WING26 chart matches WINFUT quote (same underlying)
    // This enables subscription consolidation - one SignalR sub serves all WIN family charts
    const symbolMatches =
      chart.symbol === quote.S ||
      marketdata.isSameFamily(chart.symbol, quote.S);

    const currentData = chartData[chart.id];
    if (symbolMatches && !chart.isLoading && currentData?.data) {
      // History↔RT seam: while a chart is reconciling, route its coalesced
      // stream through the dedup-by-ON path instead of a plain merge. Only when
      // ARMED — a present-but-unarmed reconciler can be observed here when a
      // superseded load's `finally` flips isLoading=false before the current
      // load has resolved history (rapid reload / periodicity change). Routing
      // to an unarmed reconciler (X=null) would release it prematurely; fall
      // back to a plain merge until it arms.
      const seamRec = seamReconcilers.get(chart.id);
      const changeType =
        seamRec && seamRec.armed
          ? reconcileSeamCoalesced(seamRec, currentData.data, quote, chart)
          : mergeQuotesInPlace(
              currentData.data,
              quote,
              periodicity || chart.periodicity,
              chart.symbol,
            );

      if (changeType) {
        // Only clone map on first change detected
        if (!newChartData) {
          newChartData = { ...chartData };
        }
        // Return new chartData entry with incremented dataVersion
        // Data array reference stays the same, but version changes to trigger re-render
        newChartData[chart.id] = {
          ...currentData,
          dataVersion: (currentData.dataVersion || 0) + 1,
        };

        // Issue #163 — follow-with-offset. A new bar was appended ('added').
        // If the user parked the chart to the left (last bar on screen with
        // whitespace to its right), slide the stored viewport by the one new
        // bar so the gap to the price scale stays constant — instead of letting
        // the last bar drift rightward into the scale (the #168 fix only kept
        // the viewport from snapping back; it left it FIXED).
        //
        // Excluded by design:
        //  - flush-right (e === prevLastIndex, no whitespace): ChartCanvas's own
        //    follow already slides it, so shifting here would double-advance.
        //  - history scroll (last bar off screen, e < prevLastIndex): leave the
        //    user where they parked.
        if (changeType === "added" && Array.isArray(chart.xExtents)) {
          const newLastIndex = currentData.data.length - 1;
          const prevLastIndex = newLastIndex - 1; // exactly one bar per 'added'
          const [s, e] = chart.xExtents;
          if (prevLastIndex >= 0 && s <= prevLastIndex && e > prevLastIndex) {
            if (!newCharts) newCharts = [...charts];
            newCharts[i] = { ...chart, xExtents: [s + 1, e + 1] };
          }
        }
      }
    }
  }

  // Return same references when nothing changed — prevents unnecessary Zustand
  // subscriber triggers (charts stays === input unless a viewport had to follow).
  return {
    chartData: newChartData || chartData,
    charts: newCharts || charts,
  };
}

/**
 * handleQuote - LEGACY VERSION (kept for backward compatibility)
 *
 * Uses mergeQuotesInPlace() to mutate data array instead of creating new arrays.
 * Increments dataVersion to trigger React re-renders without changing data reference.
 *
 * @deprecated Use handleQuoteData() for better performance (data separation)
 */
export function handleQuote(quote, charts, periodicity) {
  // PERFORMANCE: Use for loop and only clone array if changes detected
  // This prevents Zustand from triggering subscribers when quote doesn't affect any chart
  let newCharts = null;

  for (let i = 0; i < charts.length; i++) {
    const chart = charts[i];
    // Family-aware matching: WING26 chart matches WINFUT quote (same underlying)
    // This enables subscription consolidation - one SignalR sub serves all WIN family charts
    const symbolMatches =
      chart.symbol === quote.S ||
      marketdata.isSameFamily(chart.symbol, quote.S);

    if (symbolMatches && !chart.isLoading && chart.data) {
      // Mutate data array in-place for performance
      const changeType = mergeQuotesInPlace(
        chart.data,
        quote,
        periodicity || chart.periodicity,
        chart.symbol,
      );

      if (changeType) {
        // Only clone array on first change detected
        if (!newCharts) {
          newCharts = [...charts];
        }
        // Return new chart object with incremented dataVersion
        // Data array reference stays the same, but version changes to trigger re-render
        newCharts[i] = {
          ...chart,
          dataVersion: (chart.dataVersion || 0) + 1,
        };
      }
    }
  }

  // Return same reference if no changes - prevents unnecessary Zustand subscriber triggers
  return newCharts || charts;
}

/**
 * handleQuoteLegacy - LEGACY VERSION (kept for fallback)
 *
 * Uses the old mergeQuotes() that creates new arrays.
 * Only use this if issues are found with the in-place version.
 *
 * @deprecated Use handleQuote() for better performance
 */
export function handleQuoteLegacy(quote, charts, periodicity) {
  return charts.map((chart) => {
    // Family-aware matching: WING26 chart matches WINFUT quote (same underlying)
    const symbolMatches =
      chart.symbol === quote.S ||
      marketdata.isSameFamily(chart.symbol, quote.S);

    if (symbolMatches && !chart.isLoading) {
      let newData = mergeQuotes(
        chart.data,
        quote,
        periodicity || chart.periodicity,
        chart.symbol,
      );
      if (newData) {
        return { ...chart, data: newData };
      }
    }
    return chart;
  });
}
